import React from 'react';
import { Box, Container, Typography, Button, Grid, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import BrushIcon from '@mui/icons-material/Brush';

const Hero = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const features = [
    {
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      title: t('hero.features.development'),
      description: 'React, Next.js, TypeScript'
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      title: t('hero.features.architecture'),
      description: 'Cloud Hosting, WHM/cPanel'
    },
    {
      icon: <BrushIcon sx={{ fontSize: 40 }} />,
      title: t('hero.features.design'),
      description: 'Process Automation, Bot Development'
    }
  ];

  return (
    <Box
      id="home"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: 'linear-gradient(135deg, #050810 0%, #0a1428 100%)',
        overflow: 'hidden',
        pt: { xs: 8, md: 0 }
      }}
    >
      {/* Background effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(34, 225, 101, 0.1) 0%, transparent 50%)',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    mb: 2,
                    letterSpacing: '1px'
                  }}
                >
                  {t('hero.welcome')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #22e165 90%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2.5rem', md: '3.5rem' }
                  }}
                >
                  {t('hero.greeting')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h2"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mb: 3,
                    fontSize: { xs: '1.5rem', md: '2rem' }
                  }}
                >
                  {t('hero.title')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 4,
                    maxWidth: '600px',
                    lineHeight: 1.8
                  }}
                >
                  {t('hero.description')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToAbout}
                    sx={{
                      background: 'linear-gradient(45deg, #22e165 30%, #1a8f3d 90%)',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #1a8f3d 30%, #22e165 90%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(34, 225, 101, 0.3)'
                      }
                    }}
                  >
                    {t('hero.cta')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    href="#contact"
                    sx={{
                      borderColor: 'rgba(34, 225, 101, 0.5)',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#22e165',
                        background: 'rgba(34, 225, 101, 0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {t('hero.contact')}
                  </Button>
                </Box>
              </motion.div>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: 3,
                  mt: { xs: 4, md: 0 }
                }}
              >
                {features.map((feature, index) => (
                  <motion.div key={index} variants={itemVariants}>
                    <Box
                      sx={{
                        background: 'rgba(34, 225, 101, 0.05)',
                        borderRadius: 4,
                        p: 3,
                        height: '100%',
                        border: '1px solid rgba(34, 225, 101, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          background: 'rgba(34, 225, 101, 0.1)',
                          borderColor: 'rgba(34, 225, 101, 0.2)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.primary.main,
                          mb: 2
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)'
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          style={{ textAlign: 'center', marginTop: '4rem' }}
        >
          <Button
            onClick={scrollToAbout}
            sx={{
              color: 'white',
              '&:hover': {
                color: theme.palette.primary.main
              }
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 40 }} />
          </Button>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Hero; 